import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLotSizingLinesBySupplier } from '@/features/lot-sizing/hooks/use-lot-sizing-lines-by-supplier';
import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';

const ROOT_MARGIN = '200px';

type UseLotSizingSupplierLinesArgs = {
  lotSizingId: string;
  supplierId: string;
};

export function useLotSizingSupplierLines({
  lotSizingId,
  supplierId,
}: UseLotSizingSupplierLinesArgs) {
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isFetching } =
    useLotSizingLinesBySupplier(lotSizingId, supplierId);

  const { ref: loadMoreRef, inView } = useInView({
    rootMargin: ROOT_MARGIN,
    triggerOnce: false,
  });

  useEffect(() => {
    if (!(inView && hasNextPage) || isFetchingNextPage) {
      return;
    }
    fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const items: LotSizingLine[] = useMemo(
    () => data.pages.flatMap((page) => page.data ?? []),
    [data.pages],
  );

  const total = data.pages[0]?.total ?? 0;
  const isEligibleToUpdate = data.pages[0]?.isEligibleToUpdate ?? true;
  const hasMore = Boolean(hasNextPage);
  const isFetchingLines = isFetching || isFetchingNextPage;

  return {
    items,
    total,
    isEligibleToUpdate,
    hasMore,
    isFetching: isFetchingLines,
    loadMoreRef,
  };
}
